/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-nested-ternary */
import PropTypes from 'prop-types';
import { useImperativeHandle, forwardRef, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import moment from 'moment';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import { updateError } from '../../redux/slices/user';
import ReminderPresetSelector from '../ReminderPresetSelector';
import { FormProvider, RHFTextField } from '../hook-form';
import { useSelector, dispatch } from '../../redux/store';
import { saveNewLeadDetail, updateCompleteStatus, addNotes, addScheduleReminder } from '../../redux/slices/lead';
import { FollowUpSchema } from '../../validations/validations';

const FollowUp = forwardRef(({ handleClose, type }, ref) => {
    const { createLead, followLead } = useSelector((state) => state.lead);
    const [activeTime, setActiveTime] = useState('custom reminder');
    const { practiceId } = useSelector((state) => state.practiceTreatmentList);
    const [assignToState, setAssignToState] = useState([]);

    const { currentTreatmentDataGlobal } = useSelector((state) => state.multiTreatment);

    useImperativeHandle(
        ref,
        () => ({
            async submit(status) {
                await handleSubmit((data) => onSubmit(data, status))();
            },
        }),
        [activeTime, assignToState]
    );

    const defaultValues = {
        reminder: 'CALL',
        reminderDateTime: null,
        notes: '',
        otherNotes: '',
        customReminder: '',
    };

    const methods = useForm({
        resolver: yupResolver(FollowUpSchema),
        defaultValues
    });

    const onSubmit = async (data, status) => {
        if (getValues('reminder') === 'no-reminder') {
            if (status?.toLowerCase?.() === 'finish' && type === 'ConsultationAttended') {
                const leadNoteData = {
                    lead: followLead && followLead.id || createLead && createLead.id || '',
                    note: 'FOLLOW_UP',
                    treatment: currentTreatmentDataGlobal?.treatmentData?.id || (followLead?.lead_treatments?.length > 0 ? followLead.lead_treatments[0].id : '') || ''
                };
                dispatch(addNotes(leadNoteData, practiceId, handleClose, 'attended'));
                dispatch(updateCompleteStatus(true));
                return;
            }
            if (status?.toLowerCase?.() === 'finish') {
                dispatch(updateCompleteStatus(true));
                return;
            }
        }
        const leadId = followLead && followLead.id || createLead && createLead.id || '';
        if (activeTime !== '') {
            const addReminderObj = {
                note: data.notes,
                is_completed: false,
                group: 'FOLLOW_UP',
                lead: leadId,
                assignees: assignToState?.map((el) => el?.id)
            };
            if (data.reminder) {
                addReminderObj.method = data.reminder;
            }
            if (data.reminderDateTime) {
                addReminderObj.datetime = `${moment(data.reminderDateTime).format('DD-MM-YYYY HH:mm:ss')}.0Z`;
            }
            dispatch(saveNewLeadDetail({ addReminder: addReminderObj }));
            if (status?.toLowerCase?.() === 'finish') {
                dispatch(updateCompleteStatus(true));
                return;
            }
            handleClose();
        } else {
            dispatch(updateError({
                message: 'Please select tab',
                status: false
            }));
        }
    };

    const {
        control,
        setValue,
        getValues,
        handleSubmit,
        clearErrors,
    } = methods;

    const [savingReminder, setSavingReminder] = useState(false);
    // Schedule the follow-up reminder directly (posts now) — independent of the
    // attended-flow Finish / outcome gate, which doesn't reach this section.
    const handleSaveReminder = () => {
        const data = getValues();
        const leadId = followLead?.id || createLead?.id || '';
        if (!leadId) {
            dispatch(updateError({ success: false, message: 'Lead not found — please reopen and try again.' }));
            return;
        }
        if (!data.reminderDateTime) {
            dispatch(updateError({ success: false, message: 'Pick a date and time for the reminder.' }));
            return;
        }
        // Attach the treatment FK so the reminder isn't orphaned (per-treatment
        // counts + auto-complete-prior-reminders depend on it) — resolved the same
        // way the onSubmit path does.
        const treatmentId = currentTreatmentDataGlobal?.treatmentData?.id
            || (followLead?.lead_treatments?.length > 0 ? followLead.lead_treatments[0].id : undefined);
        setSavingReminder(true);
        const obj = {
            note: data.notes || '',
            is_completed: false,
            group: 'FOLLOW_UP',
            lead: leadId,
            assignees: assignToState?.map((el) => el?.id),
            method: data.reminder,
            datetime: `${moment(data.reminderDateTime).format('DD-MM-YYYY HH:mm:ss')}.0Z`,
            ...(treatmentId ? { lead_treatment: treatmentId } : {}),
        };
        dispatch(addScheduleReminder(obj, practiceId, ({ success, message }) => {
            setSavingReminder(false);
            dispatch(updateError({
                success: !!success,
                message: success
                    ? `Follow-up reminder scheduled for ${moment(data.reminderDateTime).format('ddd D MMM, HH:mm')}`
                    : (message || 'Failed to schedule reminder'),
            }));
            if (success && handleClose) handleClose();
        }));
    };

    useEffect(() => {
        setValue('customReminder', activeTime);
    }, [activeTime]);

    return (
        <Box sx={{ width: '100%' }}>
            <Typography sx={{ fontSize: '17px', fontWeight: 700, color: '#1d1d1f', letterSpacing: '-0.022em', mb: 0.5 }}>
                Schedule a follow-up
            </Typography>
            <Typography sx={{ fontSize: '13px', color: '#6e6e73', letterSpacing: '-0.01em', mb: 2.5 }}>
                Pick when to reach out next, who&apos;s assigned, and add any context.
            </Typography>
            <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                <ReminderPresetSelector
                    control={control}
                    setValue={setValue}
                    clearErrors={clearErrors}
                    activeTime={activeTime}
                    onPresetChange={setActiveTime}
                    assignToState={assignToState}
                    setAssignTo={setAssignToState}
                    showStepBadges={false}
                />
                {activeTime !== 'no-reminder' &&
                    <Box sx={{ width: '100%', mt: 2 }}>
                        <Typography sx={{ fontSize: '11px', color: '#6e6e73', letterSpacing: '-0.005em', mb: 0.75 }}>
                            Notes
                        </Typography>
                        <RHFTextField
                            sx={{
                                width: { xs: '100%', md: '60%' },
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                    bgcolor: '#ffffff',
                                    fontSize: '13px',
                                    '& fieldset': { borderColor: 'rgba(0,0,0,0.12)' },
                                    '&:hover fieldset': { borderColor: 'rgba(0,0,0,0.20)' },
                                    '&.Mui-focused fieldset': { borderColor: '#0071e3', borderWidth: '1.5px' },
                                },
                            }}
                            name="notes"
                            multiline
                            rows={4}
                            placeholder="Add any context for the follow-up..."
                        />
                    </Box>
                }
                {activeTime !== 'no-reminder' &&
                    <Box sx={{ width: '100%', mt: 2 }}>
                        <Button
                            variant="contained"
                            disableElevation
                            disabled={savingReminder}
                            onClick={handleSaveReminder}
                            startIcon={savingReminder ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : null}
                            sx={{ textTransform: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 600, bgcolor: '#0071e3', '&:hover': { bgcolor: '#0064c8' } }}
                        >
                            {savingReminder ? 'Scheduling…' : 'Schedule reminder'}
                        </Button>
                    </Box>
                }
            </FormProvider>
        </Box>
    );
});

FollowUp.propTypes = {
    handleClose: PropTypes.func,
    type: PropTypes.string,
};

export default FollowUp;